import { AccountInfo } from '@azure/msal-browser';
import { AppDispatch } from '../../redux/store';
import { setActiveUserInfo, setIsServiceUnavailable } from '../../redux/features/app/appSlice';
import { createProviderServices } from '../../services/chat/createProviderServices';
import { loadInitialChatDataHandler, loadInitialChatDataForRestrictedUserHandler } from '../../application/chat-sessions/handlers/LoadInitialChatDataHandler';
import { loadInitialUserDataHandler } from '../../application/users/handlers/LoadInitialUserDataHandler';
import { setSessions } from '../../redux/features/chatSessions/chatSessionsSlice';
import { setAgents } from '../../redux/features/agents/agentsSlice';
import { setHasMoreMessages, setIsLoadingMessages, setLoadedMessageCount, setSelectedSessionId } from '../../redux/features/chatUi/chatUiSlice';
import { setMessages } from '../../redux/features/chatMessages/chatMessagesSlice';
import { setDocumentsBySessionId } from '../../redux/features/chatDocuments/chatDocumentsSlice';
import { setPinnedAgentIds } from '../../redux/features/users/usersSlice';
import { UserRole } from '../../domain/users/models/User';

import { setChatPersonalization } from '../../redux/features/chatPersonalization/chatPersonalizationSlice';
import { alerts } from '../../shared/ui/Alerts';
import { setTelemetryUserContext } from '../telemetry/appInsights';

export interface InitializeChatViewArgs {
    activeAccount: AccountInfo;
    accessToken: string;
    nexusAccessToken: string;
    dispatch: AppDispatch;
}

export interface InitializeChatViewResult {
    succeeded: boolean;
}

export const initializeChatView = async ({
    activeAccount,
    accessToken,
    nexusAccessToken,
    dispatch,
}: InitializeChatViewArgs): Promise<InitializeChatViewResult> => {
    const accountId =
        activeAccount.idTokenClaims?.oid && activeAccount.idTokenClaims?.tid
            ? `${activeAccount.idTokenClaims.oid}.${activeAccount.idTokenClaims.tid}`
            : activeAccount.homeAccountId;

    dispatch(
        setActiveUserInfo({
            id: accountId,
            email: activeAccount.username,
            username: activeAccount.name ?? activeAccount.username,
            role: UserRole.Standard,
        }),
    );

    setTelemetryUserContext(accountId, activeAccount.tenantId);

    const providerServices = createProviderServices();

    // Load user data and personalization first to determine user role
    const [userDataResult, personalizationResult] = await Promise.allSettled([
        loadInitialUserDataHandler({
            userId: accountId,
            accessToken,
            providerServices,
        }),
        providerServices.proGPT.personalization.getChatPersonalizationAsync(accessToken),
    ]);

    // Determine user role from loaded data
    let userRole = UserRole.Standard;
    
    if (userDataResult.status === 'fulfilled') {
        userRole = userDataResult.value.role ?? UserRole.Standard;
        // Dont want to show Pinned Special Agents for restricted users
        dispatch(userRole !== UserRole.RestrictedAccess ? setPinnedAgentIds(userDataResult.value.pinnedAgents ?? []) : setPinnedAgentIds([]));

        dispatch(
            setActiveUserInfo({
                id: accountId,
                email: activeAccount.username,
                username: activeAccount.name ?? activeAccount.username,
                role: userRole,
                photoUrl: userDataResult.value.photoUrl,
            }),
        );
    } else {
        alerts.error('Failed to load user preferences.');
    }


    if (personalizationResult.status === 'fulfilled') {
        dispatch(setChatPersonalization(personalizationResult.value));
    } else {
        alerts.error('Failed to load chat personalization.');
    }

    // Load chat data based on user role
    const chatDataResult = await (async () => {
        if (userRole === UserRole.RestrictedAccess) {
            return loadInitialChatDataForRestrictedUserHandler({
                accessToken,
                nexusAccessToken,
                providerServices,
            });
        } else {
            return loadInitialChatDataHandler({
                accessToken,
                nexusAccessToken,
                providerServices,
            });
        }
    })();

    dispatch(setSessions(chatDataResult.sessions));
    dispatch(setAgents(chatDataResult.agents));
    dispatch(setDocumentsBySessionId(chatDataResult.documentsBySessionId));
    dispatch(setSelectedSessionId(chatDataResult.selectedSessionId));

    if (chatDataResult.isServiceUnavailable) {
        alerts.error('There was an issue retrieving your chat sessions. Please contact support.');
        dispatch(setIsServiceUnavailable(true));
    } else {
        dispatch(setIsServiceUnavailable(false));
    }

    for (const error of chatDataResult.providerLoadErrors) {
        alerts.error(error);
    }

    if (chatDataResult.selectedSessionId) {
        dispatch(
            setIsLoadingMessages({
                sessionId: chatDataResult.selectedSessionId,
                value: true,
            }),
        );

        dispatch(
            setMessages({
                sessionId: chatDataResult.selectedSessionId,
                messages: chatDataResult.selectedSessionMessages,
            }),
        );

        dispatch(setHasMoreMessages({ sessionId: chatDataResult.selectedSessionId, value: chatDataResult.selectedSessionHasMoreMessages }));
        dispatch(setLoadedMessageCount({ sessionId: chatDataResult.selectedSessionId, value: chatDataResult.selectedSessionMessages.length }));

        dispatch(
            setIsLoadingMessages({
                sessionId: chatDataResult.selectedSessionId,
                value: false,
            }),
        );
    }

    return {
        succeeded: true,
    };
};

export default initializeChatView;
